import useSideNavPresenter from '@Screens/Main/sideNavPresenter';
import Constants from 'expo-constants';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React from 'react';
import { Dimensions } from 'react-native';
import { Portal } from 'react-native-paper';
import styled from 'styled-components/native';

import AboutToscan from '../../../assets/AboutToscan';
import OwnableTheme from '../../../assets/OwnableTheme';
import CloseButton from '../../Common/CloseButton';
import { Text } from '../../Common/Text';

import { useProductsStore } from '~stores/products';

const StyledAboutOverlay = styled.View`
  position: absolute;
  z-index: 1000;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const StyledAboutDialog = styled.View`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  padding: 48px 32px 32px 32px;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  background-color: white;
  box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.3);
`;

const StyledScrollContainer = styled.ScrollView``;

const StyledCloseContainer = styled.View`
  margin-top: -6px;
`;

const StyledRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
`;

const AboutOverlay: FC = () => {
  const { uiAboutPanelOpen } = useProductsStore();
  const { handleAboutClick } = useSideNavPresenter();

  // By extracting the first paragraphs and first title, we can pin that to the top of the panel
  // This helps it stay outside the main scroll content
  const [{ paragraphs: firstParagraphs, title: firstTitle }, ...rest] =
    AboutToscan.sections;

  const {
    paragraphs: restParagraphs,
    title: restTitle,
    subsections: restSubsections,
  } = rest?.[0] ?? [];

  if (!uiAboutPanelOpen) {
    return null;
  }

  return (
    <Portal>
      <StyledAboutOverlay>
        <StyledAboutDialog>
          <StyledRow>
            <Text weight='bold' color={OwnableTheme.colors.black}>
              {firstTitle}
              {'\n\n'}
            </Text>
            <StyledCloseContainer>
              <CloseButton
                onPress={handleAboutClick}
                size='small'
                iconColor={OwnableTheme.colors.black}
              />
            </StyledCloseContainer>
          </StyledRow>
          <StyledScrollContainer
            style={{
              maxHeight: Dimensions.get('window').height * 0.5,
            }}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
          >
            {firstParagraphs.map((paragraph) => (
              <Text weight='regular' color={OwnableTheme.colors.black}>
                {paragraph}
                {'\n\n'}
              </Text>
            ))}
            {restParagraphs.map((paragraph) => (
              <Text weight='regular' color={OwnableTheme.colors.black}>
                {paragraph}
                {'\n\n'}
              </Text>
            ))}
            <Text weight='bold' color={OwnableTheme.colors.black}>
              {restTitle}
              {'\n\n'}
            </Text>
            {(restSubsections ?? []).map((subsection) => (
              <React.Fragment
                key={subsection?.title || subsection?.paragraphs?.length}
              >
                {subsection.title && (
                  <Text weight='bold' color={OwnableTheme.colors.black}>
                    {subsection.title}
                  </Text>
                )}
                {subsection.paragraphs.map((paragraph) => (
                  <Text weight='regular' color={OwnableTheme.colors.black}>
                    {paragraph}
                    {'\n\n'}
                  </Text>
                ))}
              </React.Fragment>
            ))}
            <Text gutterBottom={20} align='center'>
              Version:{' '}
              {`${Constants.manifest?.version?.slice(1)}${
                Constants.manifest?.extra?.revision || ''
              }${Constants.manifest?.extra?.environmentName ? ' ' : ''}${
                Constants.manifest?.extra?.environmentName || ''
              }`}
            </Text>
          </StyledScrollContainer>
        </StyledAboutDialog>
      </StyledAboutOverlay>
    </Portal>
  );
};

export default observer(AboutOverlay);
